import {
  Button,
  Icon,
  Typography,
  VerticalDivider,
  Image,
  Tooltip
} from '@smartgift/frontend-toolkit';
import dayjs from 'dayjs';
import get from 'lodash/get';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Card } from '@hero/components/atoms';
import { GIFT_STATUSES, PRODUCT_TYPES } from '@hero/constants';
import { useSendRemindersNotification } from '@hero/hooks';
import { PageLayout } from '@hero/layouts';
import { DetailGroup, DetailItem, OrderStatusBadge } from '@hero/molecules';
import {
  useGetAutomation,
  useGetAutomationOrder,
  useSendRemindersAutomation
} from '@hero/services/order-service';
import { useNotification } from '@hero/store/hooks';
import { getName, getSafeInnerHtml } from '@hero/utils';
import { getDiffDatesWithFormatted } from '@hero/utils/dateUtils';
import { formatCurrency, formatDate, getGiftStatus } from '@hero/utils/helper';

import * as S from './style';

const GIFT_STATUS_LIST = [
  GIFT_STATUSES.emailSent,
  GIFT_STATUSES.emailOpened,
  GIFT_STATUSES.giftOpened,
  GIFT_STATUSES.giftRedeemed,
  GIFT_STATUSES.giftDelivered
];

function AutomationRecipientDetail() {
  const { t } = useTranslation();
  const { showSuccess } = useNotification();
  const { orderCode, code } = useParams();
  const [copied, setCopied] = useState(false);

  const { data: automation } = useGetAutomation({ code }, { enabled: !!code });
  const { data: order } = useGetAutomationOrder({ code, orderCode }, { enabled: !!orderCode });

  const {
    status,
    giftStatus,
    recipient,
    deliveryInfo,
    merchantOrder,
    experience,
    selectedSku,
    link,
    timeline
  } = order || {};

  const onCopyLinkClick = () => {
    navigator.clipboard.writeText(link?.shortened);
    setCopied(true);

    showSuccess({ type: 'toast', message: t('uniqueGiftLinkSuccessfullyCopied') });
  };

  const giftStatusValue = React.useMemo(
    () => status && getGiftStatus({ status, giftStatus, deliveryDate: deliveryInfo?.deliveryDate }),
    [status, giftStatus, deliveryInfo]
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (copied) setCopied(false);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [copied]);

  if (status) {
    status.deliveredAt = deliveryInfo?.deliveryDate;
  }

  const giftStatusLabel = (
    <S.GiftStatusGroup>
      <Typography variant="body1Bold">{t('giftStatus')}</Typography>
      <OrderStatusBadge status={giftStatusValue}></OrderStatusBadge>
    </S.GiftStatusGroup>
  );

  const TableHeaderButton = () => {
    const { send } = useSendRemindersNotification({
      useSendReminder: useSendRemindersAutomation
    });

    const handleSendReminder = () => {
      send(code, [orderCode]);
    };

    return (
      !status?.accepted && (
        <S.TableHeaderButtonWrapper>
          <VerticalDivider $height={24} $margin="0 8px" />
          <Button variant="link" onClick={handleSendReminder} disabled={status?.accepted}>
            <Icon name="email" color="var(--color-accent-primary-default)" />
            {t('remindRecipient')}
          </Button>
          <VerticalDivider $height={24} $margin="0 8px" />
          <Button variant="link" onClick={onCopyLinkClick} disabled={status?.accepted}>
            {!copied && (
              <Icon
                name="copy-paste"
                color="var(--color-accent-primary-default)"
                style={{
                  strokeDasharray: 50,
                  strokeDashoffset: copied ? -50 : 0,
                  transition: 'all 300ms ease-in-out'
                }}
              />
            )}
            {copied && (
              <Icon
                name="success"
                color="var(--color-accent-primary-default)"
                style={{
                  strokeDasharray: 50,
                  strokeDashoffset: copied ? 0 : -50,
                  transition: 'all 300ms ease-in-out'
                }}
              />
            )}
            {copied ? t('copied') : t('copyUniqueGiftLink')}
          </Button>
        </S.TableHeaderButtonWrapper>
      )
    );
  };

  return (
    <PageLayout
      header={{
        title: t('giftActivityDetails'),
        backButton: true
      }}
    >
      <S.RecipientDetailWrapper>
        <S.CardHeader>
          <Icon name="contacts" color="var(--color-accent-primary-default)" />
          <Typography variant="body1Bold">
            {getName({ firstName: recipient?.firstName, lastName: recipient?.lastName })}
          </Typography>
          <TableHeaderButton />
        </S.CardHeader>
        <S.PaperWrapper>
          <Card>
            <DetailGroup title={t('contactInformation')}>
              <DetailItem
                title={`${t('firstName')}:`}
                editable={false}
                withBorder
                bodyStyle={{ padding: '0 0 16px' }}
                titleStyle={{ width: 120 }}
              >
                {recipient?.firstName}
              </DetailItem>
              <DetailItem
                title={`${t('lastName')}:`}
                editable={false}
                withBorder
                bodyStyle={{ padding: '16px 0' }}
                titleStyle={{ width: 120 }}
              >
                {recipient?.lastName}
              </DetailItem>
              <DetailItem
                title={`${t('email')}:`}
                editable={false}
                isCTADisabled
                bodyStyle={{ padding: '16px 0' }}
                titleStyle={{ width: 120 }}
              >
                {recipient?.email}
              </DetailItem>
            </DetailGroup>
            <DetailGroup title={t('deliveryInformation')}>
              <DetailItem
                title={`${t('firstName')}:`}
                withBorder
                bodyStyle={{ padding: '0 0 16px' }}
                titleStyle={{ width: 120 }}
              >
                {deliveryInfo?.firstName ? (
                  deliveryInfo?.firstName
                ) : (
                  <Typography variant="body2" emphasis="weaker">
                    (
                    {t('giftRecipientNamePlaceholder', {
                      name: t('firstName')
                    }).toLocaleLowerCase()}
                    )
                  </Typography>
                )}
              </DetailItem>
              <DetailItem
                title={`${t('lastName')}:`}
                withBorder
                bodyStyle={{ padding: '16px 0' }}
                titleStyle={{ width: 120 }}
              >
                {deliveryInfo?.lastName ? (
                  deliveryInfo?.lastName
                ) : (
                  <Typography variant="body2" emphasis="weaker">
                    (
                    {t('giftRecipientNamePlaceholder', {
                      name: t('lastName')
                    }).toLocaleLowerCase()}
                    )
                  </Typography>
                )}
              </DetailItem>
              <DetailItem
                title={`${t('address')}:`}
                bodyStyle={{ padding: '16px 0 24px' }}
                titleStyle={{ width: 120 }}
              >
                {deliveryInfo?.address?.addressLine1 ? (
                  <>
                    {`${deliveryInfo?.address?.city}, ${deliveryInfo?.address?.stateAbbv} ${deliveryInfo?.address?.postalCode}`}
                  </>
                ) : (
                  <Typography variant="body2" emphasis="weaker">
                    (
                    {t('giftRecipientNamePlaceholder', {
                      name: t('address')
                    }).toLocaleLowerCase()}
                    )
                  </Typography>
                )}
              </DetailItem>
              <Typography variant="caption1" emphasis="weaker">
                *{t('addressDescriptionText', { name: deliveryInfo?.firstName || '{{firstName}}' })}
              </Typography>
              {deliveryInfo?.deliveryDate && (
                <DetailItem
                  title={`${t('deliveryDate')}:`}
                  withBorder={!!deliveryInfo?.merchantOrderId}
                  titleStyle={{ width: 120 }}
                >
                  {formatDate(deliveryInfo?.deliveryDate, { includeTime: false })}
                </DetailItem>
              )}
              {deliveryInfo?.merchantOrderId && !merchantOrder?.trackingCode && (
                <DetailItem title={`${t('merchantOrderId')}:`} titleStyle={{ width: 120 }}>
                  {deliveryInfo?.merchantOrderId}
                </DetailItem>
              )}
              {merchantOrder?.trackingCode && (
                <DetailItem title={`${t('trackingCode')}:`}>
                  {merchantOrder?.trackingCode}
                </DetailItem>
              )}
              {merchantOrder?.trackingLink && (
                <DetailItem title={`${t('trackingLink')}:`}>
                  {merchantOrder?.trackingLink}
                </DetailItem>
              )}
            </DetailGroup>
          </Card>
          <Card style={{ flexDirection: 'row', gap: 16, padding: '4px 4px 4px 16px' }}>
            <S.GiftStatus>
              <DetailGroup header={giftStatusLabel}>
                {selectedSku?.type === 'charity' ? (
                  <S.RecipientCharity>
                    <S.RecipientCharityImage>
                      <Image
                        src={selectedSku?.images[0].url}
                        alt=""
                        width={120}
                        height={130}
                        style={{ borderRadius: 'var(--radius-m)' }}
                      />
                      <S.IconWrapper>
                        <Tooltip content={t('charityIconTooltipContent')}>
                          <Icon name="f-heart" color="var(--color-error-default)" />
                        </Tooltip>
                      </S.IconWrapper>
                    </S.RecipientCharityImage>
                    <S.RecipientCharityBody>
                      <Typography variant="caption1Bold" emphasis="weaker">
                        {selectedSku?.name}
                      </Typography>
                      <S.RecipientCharityDescription>
                        <div {...getSafeInnerHtml(selectedSku?.shortDesc)} />
                      </S.RecipientCharityDescription>
                    </S.RecipientCharityBody>
                  </S.RecipientCharity>
                ) : (
                  <S.SelectedItem isNonEmpty={selectedSku?.name}>
                    <Image
                      src={get(selectedSku, 'images[0].url')}
                      alt=""
                      width={80}
                      style={{
                        objectFit:
                          selectedSku?.meta?.productType === PRODUCT_TYPES.GIFT_CARD
                            ? 'contain'
                            : 'inherit'
                      }}
                    />
                    {selectedSku?.name ? (
                      <S.ProductDetail>
                        <S.ProductDetailHeader>
                          <div>
                            <Typography variant="caption1Bold" emphasis="weaker">
                              {selectedSku?.merchant?.name}
                            </Typography>
                            <Typography
                              as="div"
                              variant="caption2Bold"
                              emphasis="weaker"
                              style={{ display: 'flex', alignItems: 'center' }}
                            >
                              {t('code')}:&nbsp;
                              <Typography variant="caption1">{selectedSku?.sku}</Typography>
                            </Typography>
                          </div>
                          <Typography variant="body2Bold">
                            {formatCurrency(selectedSku.price)}
                          </Typography>
                        </S.ProductDetailHeader>
                        <Typography variant="body2">{selectedSku?.name}</Typography>
                      </S.ProductDetail>
                    ) : (
                      <Typography
                        variant="body2Italic"
                        emphasis="weak"
                        style={{ textAlign: 'right' }}
                      >
                        (
                        {t('nameGiftSelectionWillShowHere', {
                          name: recipient?.firstName
                        })}
                        )
                      </Typography>
                    )}
                  </S.SelectedItem>
                )}
                <DetailItem title={`${t('orderCode')}:`} titleStyle={{ width: 120 }}>
                  <Typography variant="body2Bold" style={{ textAlign: 'right' }}>
                    {orderCode}
                  </Typography>
                </DetailItem>
                <DetailItem title={`${t('regifted')}:`} titleStyle={{ width: 120 }}>
                  {deliveryInfo?.recipientType ? (
                    <Typography variant="body2Bold" color="success" style={{ textAlign: 'right' }}>
                      {t(deliveryInfo?.recipientType !== 'myself' ? 'yes' : 'no')}
                    </Typography>
                  ) : (
                    <Typography variant="body2" emphasis="weak" style={{ textAlign: 'right' }}>
                      (
                      {t('reGiftedWillShowHere', {
                        name: recipient?.firstName
                      })}
                      )
                    </Typography>
                  )}
                </DetailItem>
                <DetailItem
                  title={`${t('acceptanceTime')}:`}
                  className={{
                    'non-empty': !!status?.accepted
                  }}
                  withBorder
                  titleStyle={{ width: 120 }}
                >
                  {!!status?.accepted && !!status?.opened ? (
                    <Tooltip
                      content={getDiffDatesWithFormatted({
                        start: status?.openedAt,
                        end: status?.acceptedAt,
                        isFull: true
                      })}
                      tooltipConfig={{ placement: 'top' }}
                    >
                      <Typography variant="body2Bold" style={{ textAlign: 'right', width: 176 }}>
                        {getDiffDatesWithFormatted({
                          start: status?.openedAt,
                          end: status?.acceptedAt
                        })}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography
                      variant="body2Italic"
                      emphasis="weak"
                      style={{ textAlign: 'right' }}
                    >
                      0d, 0h, 0m, 0s
                    </Typography>
                  )}
                </DetailItem>
                <DetailItem
                  title={`${t('name')}:`}
                  withBorder
                  bodyStyle={{ padding: '16px 0' }}
                  titleStyle={{ width: 120 }}
                >
                  {automation?.name}
                </DetailItem>
                <DetailItem
                  title={`${t('giftMessage')}:`}
                  withBorder
                  bodyStyle={{ padding: '16px 0' }}
                  titleStyle={{ width: 120 }}
                >
                  {experience?.giftMessage && (
                    <div {...getSafeInnerHtml(experience?.giftMessage)} />
                  )}
                </DetailItem>
                <DetailItem
                  title={`${t('tyNote')}:`}
                  bodyStyle={{ padding: '16px 0' }}
                  titleStyle={{ width: 120 }}
                >
                  {experience?.thankYouMessage ? (
                    <div {...getSafeInnerHtml(experience?.thankYouMessage)} />
                  ) : (
                    <Typography variant="body2" emphasis="weak">
                      ({t('tyNotePlaceholder', { name: recipient?.firstName })})
                    </Typography>
                  )}
                </DetailItem>
              </DetailGroup>
            </S.GiftStatus>
            <S.Timeline>
              <S.Steps>
                {timeline &&
                  GIFT_STATUS_LIST.map(giftStatus => {
                    const timeItem = timeline.find(time => time.action === giftStatus);
                    const isSelected = timeItem?.action === timeline[timeline.length - 1]?.action;

                    return (
                      <S.StepsContainer key={giftStatus} isSelected={isSelected}>
                        <S.Dot isSelected={isSelected} />
                        <S.StepContent>
                          <Typography
                            variant={isSelected ? 'body2ExtraBold' : 'body2Bold'}
                            uppercase
                            style={{ width: 60 }}
                            emphasis={isSelected ? 'default' : 'weakest2'}
                          >
                            {t(giftStatus)}
                          </Typography>
                          {timeItem && (
                            <S.StepDate>
                              <Icon name="calendar" size="sm" color="var(--color-border-strong)" />
                              <div>
                                <Typography
                                  variant={isSelected ? 'caption1Bold' : 'caption1'}
                                  style={{ opacity: 0.8 }}
                                  emphasis="weaker"
                                >
                                  {dayjs(timeItem?.date).tz('est').format('MM/DD/YYYY')}
                                </Typography>
                                <Typography
                                  variant={isSelected ? 'caption2Bold' : 'caption2'}
                                  emphasis="weaker"
                                >
                                  {dayjs(timeItem?.date).tz('est').format('h:mm A')} ({t('est')})
                                </Typography>
                              </div>
                            </S.StepDate>
                          )}
                        </S.StepContent>
                      </S.StepsContainer>
                    );
                  })}
              </S.Steps>
            </S.Timeline>
          </Card>
        </S.PaperWrapper>
      </S.RecipientDetailWrapper>
    </PageLayout>
  );
}

export { AutomationRecipientDetail };
